'use client'

import * as React from 'react'
import {
  SlidersHorizontal, X,
  Sofa, Sun, Car, PawPrint, UtensilsCrossed, Tv,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSearchStore, useKeywordsStore } from '@/lib/store'
import { STATIC_FILTER_TAGS } from '@/lib/constants'
import type { FilterTag, PropertyType } from '@/lib/types'
import { api } from '@/lib/api'
import { FilterModal } from './filter-modal'
import { PriceModeToggle } from './price-mode-toggle'
import { CompactRangeSlider, RoomButtonGroup } from '@/components/ui'
import { useI18n } from '@/lib/i18n'
import {
  ROOMS_MIN, ROOMS_MAX,
  PRICE_MIN, PRICE_MAX, PRICE_STEP,
  AREA_MIN, AREA_MAX, AREA_STEP,
} from '@/lib/constants'

interface FilterTagsProps {
  className?: string
  onSearch?: () => void
}

// Quick access feature filters
const QUICK_FEATURE_IDS = ['furnished', 'balcony', 'parking', 'pets_allowed', 'dishwasher', 'tv']

const QUICK_FEATURE_ICONS: Record<string, LucideIcon> = {
  furnished: Sofa,
  balcony: Sun,
  parking: Car,
  pets_allowed: PawPrint,
  dishwasher: UtensilsCrossed,
  tv: Tv,
}


export function FilterTags({ className, onSearch }: FilterTagsProps) {
  const { t } = useI18n()
  const {
    activeTags, toggleTag, clearTags, filters, updateFilter,
    selectedCity, selectedDistricts,
    filterCount: matchCount, setFilterCount: setMatchCount,
    isCountLoading, setIsCountLoading,
  } = useSearchStore()
  const { filterTags } = useKeywordsStore()
  const [modalOpen, setModalOpen] = React.useState(false)

  // Rooms button group value (derived from store)
  const roomsValue: [number, number] = [
    filters.rooms_min ?? ROOMS_MIN,
    filters.rooms_max ?? ROOMS_MAX,
  ]

  const handleRoomsChange = (value: [number, number]) => {
    const [min, max] = value
    updateFilter('rooms_min', min === ROOMS_MIN ? undefined : min)
    updateFilter('rooms_max', max === ROOMS_MAX ? undefined : max)
  }

  // Slider values
  const [priceRange, setPriceRange] = React.useState<[number, number]>([
    filters.price_min ?? PRICE_MIN,
    filters.price_max ?? PRICE_MAX
  ])
  const [areaRange, setAreaRange] = React.useState<[number, number]>([
    filters.area_min ?? AREA_MIN,
    filters.area_max ?? AREA_MAX
  ])

  // Sync slider values when store changes
  React.useEffect(() => {
    setPriceRange([
      filters.price_min ?? PRICE_MIN,
      filters.price_max ?? PRICE_MAX
    ])
  }, [filters.price_min, filters.price_max])

  React.useEffect(() => {
    setAreaRange([
      filters.area_min ?? AREA_MIN,
      filters.area_max ?? AREA_MAX
    ])
  }, [filters.area_min, filters.area_max])

  // Price slider handlers
  const handlePriceChange = (value: [number, number]) => {
    setPriceRange(value)
  }

  const handlePriceCommit = (value: [number, number]) => {
    const [min, max] = value
    updateFilter('price_min', min === PRICE_MIN ? undefined : min)
    updateFilter('price_max', max === PRICE_MAX ? undefined : max)
  }

  // Area slider handlers
  const handleAreaChange = (value: [number, number]) => {
    setAreaRange(value)
  }

  const handleAreaCommit = (value: [number, number]) => {
    const [min, max] = value
    updateFilter('area_min', min === AREA_MIN ? undefined : min)
    updateFilter('area_max', max === AREA_MAX ? undefined : max)
  }

  // Format price for display
  const formatPrice = (value: number) => {
    if (value === undefined || isNaN(value)) return ''
    if (value >= PRICE_MAX) return t('search.any', 'Any')
    if (value >= 1000) return `${(value / 1000).toFixed(0)}k`
    return Math.round(value).toString()
  }

  // Format area for display
  const formatArea = (value: number) => {
    if (value === undefined || isNaN(value)) return ''
    if (value >= AREA_MAX) return t('search.any', 'Any')
    return Math.round(value).toString()
  }

  // Use store tags if available, fallback to static
  const tagsToUse = filterTags.length > 0 ? filterTags : STATIC_FILTER_TAGS

  // Get quick feature tags
  const quickFeatureTags = React.useMemo(() => {
    return QUICK_FEATURE_IDS
      .map((id) => tagsToUse.find((tag) => tag.id === id))
      .filter(Boolean) as FilterTag[]
  }, [tagsToUse])
  
  // Count of active filters not in quick access
  const additionalActiveCount = activeTags.filter(
    (id) => !QUICK_FEATURE_IDS.includes(id)
  ).length

  // Count active filters
  const filterCount =
    (filters.rooms_min ? 1 : 0) + (filters.rooms_max ? 1 : 0) +
    (filters.area_min ? 1 : 0) + (filters.area_max ? 1 : 0) +
    (filters.price_min ? 1 : 0) + (filters.price_max ? 1 : 0)

  const totalActiveCount = activeTags.length + filterCount

  // Clear all filters including numeric inputs
  const handleClearAll = () => {
    clearTags()
    updateFilter('rooms_min', undefined)
    updateFilter('rooms_max', undefined)
    updateFilter('area_min', undefined)
    updateFilter('area_max', undefined)
    updateFilter('price_min', undefined)
    updateFilter('price_max', undefined)
  }

  // Debounced match count fetch
  const countDebounceRef = React.useRef<NodeJS.Timeout>(null)
  const countAbortRef = React.useRef<AbortController>(null)

  React.useEffect(() => {
    if (totalActiveCount === 0) {
      setMatchCount(null)
      return
    }

    if (countDebounceRef.current) clearTimeout(countDebounceRef.current)
    setIsCountLoading(true)

    countDebounceRef.current = setTimeout(async () => {
      if (countAbortRef.current) countAbortRef.current.abort()
      const abortController = new AbortController()
      countAbortRef.current = abortController

      try {
        const response = await api.countListings(
          {
            filters: {
              ...filters,
              city: selectedCity || undefined,
              districts: selectedDistricts.length > 0 ? selectedDistricts : undefined,
            },
            tags: activeTags.length > 0 ? activeTags : undefined,
          },
          abortController.signal,
        )
        if (!abortController.signal.aborted) {
          setMatchCount(response.total_count)
          setIsCountLoading(false)
        }
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          setMatchCount(null)
          setIsCountLoading(false)
        }
      }
    }, 400)

    return () => {
      if (countDebounceRef.current) clearTimeout(countDebounceRef.current)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeTags, selectedCity, selectedDistricts])

  return (
    <>
      <div className={cn('space-y-3', className)}>
        {/* Row 1: Rooms, Price, Area filters */}
        <div className="flex items-center gap-2 sm:gap-4 justify-center flex-wrap sm:flex-nowrap">
          <RoomButtonGroup
            value={roomsValue}
            onChange={handleRoomsChange}
            variant="compact"
          />

          <div className={cn(
            'flex items-center rounded-full border-2 pr-1.5 transition-all',
            (filters.price_min || filters.price_max)
              ? 'bg-primary/10 border-primary'
              : 'bg-card border-border'
          )}>
            <CompactRangeSlider
              label={t('search.price', 'Price')}
              min={PRICE_MIN}
              max={PRICE_MAX}
              step={PRICE_STEP}
              value={priceRange}
              onValueChange={handlePriceChange}
              onValueCommit={handlePriceCommit}
              formatValue={formatPrice}
              unit="CZK"
              marks={3}
              scale="priceOptimized"
              borderless
            />
            <div className="w-px h-5 bg-border/50 mx-0.5" />
            <PriceModeToggle variant="compact" />
          </div>

          <CompactRangeSlider
            label={t('search.area', 'Area')}
            min={AREA_MIN}
            max={AREA_MAX}
            step={AREA_STEP}
            value={areaRange}
            onValueChange={handleAreaChange}
            onValueCommit={handleAreaCommit}
            formatValue={formatArea}
            unit="m²"
            marks={3}
            scale="areaOptimized"
          />
        </div>

        {/* Row 2: Features + All Filters button */}
        <div className="flex items-center justify-center gap-4">
          {/* Features - hidden on mobile */}
          <div className="hidden sm:flex items-center gap-2">
            <span className="text-xs text-muted-foreground font-medium">
              {t('search.features', 'Features')}
            </span>
            <div className="flex items-center gap-1.5">
              {quickFeatureTags.map((tag) => (
                <FilterTagButton
                  key={tag.id}
                  label={tag.label}
                  icon={QUICK_FEATURE_ICONS[tag.id]}
                  active={activeTags.includes(tag.id)}
                  onClick={() => toggleTag(tag.id)}
                />
              ))}
            </div>
          </div>

          {/* Filters button - always visible, shows all active count on mobile */}
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            data-tour="more-filters"
            className={cn(
            'px-4 py-2 sm:py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap',
            'border-2 border-dashed',
            'flex items-center gap-2',
              totalActiveCount > 0
                ? 'bg-primary/10 text-primary border-primary'
                : 'bg-card text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
            )}
          >
            <SlidersHorizontal className="h-4 w-4" />
            <span>{t('search.allFilters', 'All Filters')}</span>
            {totalActiveCount > 0 && (
              <span className="sm:hidden px-1.5 py-0.5 text-xs bg-primary text-primary-foreground rounded-full">
                {totalActiveCount}
              </span>
            )}
            {additionalActiveCount > 0 && (
              <span className="hidden sm:inline px-1.5 py-0.5 text-xs bg-primary text-primary-foreground rounded-full">
                +{additionalActiveCount}
              </span>
            )}
          </button>
        </div>
        
        {/* Clear all filters button */}
        {totalActiveCount > 0 && (
          <div className="hidden sm:flex items-center justify-center gap-2 text-sm">
            <button
              type="button"
              onClick={handleClearAll}
              className="text-primary hover:underline font-medium inline-flex items-center gap-1"
            >
              <X className="h-3 w-3" />
              {t('search.clearFilters', 'Clear filters')}
            </button>
          </div>
        )}
      </div>
      
      {/* Filter Modal */}
      <FilterModal 
        open={modalOpen} 
        onOpenChange={setModalOpen}
        onApply={onSearch}
      />
    </>
  )
}

interface FilterTagButtonProps {
  label: string
  active: boolean
  onClick: () => void
  icon?: LucideIcon
}

function FilterTagButton({ label, active, onClick, icon: Icon }: FilterTagButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap',
        'border-2',
        active
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-card text-foreground border-border hover:border-primary/50'
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
      {label}
    </button>
  )
}
